// App
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Lodash
import assign from 'lodash/assign';

// Actions
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import ErrorActions from 'app/shared/flux/actions/ErrorActions';
import DEPRECATED_FilterActions from 'app/shared/flux/actions/DEPRECATED_FilterActions';

// Models
import DEPRECATED_Filter from 'app/shared/models/DEPRECATED_Filter';

// Misc / utils
import {
  DEPRECATED_filterUtils_getFilterLabels,
  filterUtils_getDefaultFilterVal,
  filterUtils_getFilterDiff,
} from 'app/shared/utils/filterUtils';
import { yieldCallback } from '@zillow/yield-callback';

type Filter = Record<string, unknown>;

type RootState = {
  DEPRECATED_filter: Filter;
};

type FilterLabelCounts = Record<string, string>;

type UseDesktopLogicReturn = {
  /** Initializes the filter pills by fetching listing counts for each active filter */
  initialize: () => void;
  /** Clears all active filters and resets to default state */
  handleClearAllFilters: () => void;
  /** Removes a specific filter by its key (e.g., 'price', 'bedrooms') */
  handleFilterItemRemoval: (key: string) => void;
  /** Array of [filterKey, filterLabel] tuples representing active filters */
  filterLabels: Array<[string, string]>;
  /** Map of filter keys to the number of additional listings that would be shown if that filter was removed */
  filterLabelCounts: FilterLabelCounts;
  /** Loading state while fetching listing counts */
  isLoading: boolean;
};

/**
 * Hook that manages filter pill logic for desktop Search Results Page (SRP)
 *
 * ## Purpose
 * Provides the data and interactions for filter pills displayed above the listing results.
 * Each pill represents an active filter and shows how many additional listings would be
 * available if that specific filter were removed.
 *
 * ## Business Context
 * On desktop SRP, users see "pills" for each active filter (e.g., "2+ Bedrooms", "$1000-$2000").
 * Hovering over a pill shows "X more listings" - this communicates the cost/benefit of each filter
 * to help users understand which filters are most restrictive.
 *
 * ## How It Works
 * 1. Compares current filter state to default (no filters) to determine active filters
 * 2. For each active filter, fetches listing count with that filter removed
 * 3. Calculates difference to show "X more listings available"
 * 4. Provides handlers for removing individual filters or clearing all
 *
 * ## Usage
 * Call `initialize()` when the component mounts to fetch all listing counts in parallel.
 * Display each filterLabel as a pill with its corresponding filterLabelCount.
 *
 * @returns Object containing filter state, loading state, and interaction handlers
 *
 * @example
 * const {
 *   initialize,
 *   filterLabels,
 *   filterLabelCounts,
 *   handleFilterItemRemoval,
 *   isLoading
 * } = useDesktopLogic();
 *
 * useEffect(() => {
 *   initialize();
 * }, []);
 *
 * // Render pills
 * {filterLabels.map(([key, label]) => (
 *   <FilterPill
 *     key={key}
 *     label={label}
 *     count={filterLabelCounts[key]}
 *     onRemove={() => handleFilterItemRemoval(key)}
 *   />
 * ))}
 */
export const useDesktopLogic = (): UseDesktopLogicReturn => {
  // Redux
  const dispatch = useDispatch();
  const desktopFilter = useSelector((state: RootState) => state.DEPRECATED_filter);

  // React
  const [filterLabelCounts, setFilterLabelCounts] = useState<FilterLabelCounts>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const desktopFilterLabels = filterUtils_getFilterDiff({
    baseFilter: DEPRECATED_Filter.create(),
    editedFilter: desktopFilter,
  }) as Record<string, unknown>;
  const keys = Object.keys(desktopFilterLabels);
  const filterLabels: Array<[string, string]> = keys.map((key) => {
    // @ts-expect-error - DEPRECATED_filterUtils_getFilterLabels has loose typing
    const val = DEPRECATED_filterUtils_getFilterLabels(key, desktopFilterLabels[key]);
    return [key, String(val)];
  });

  /**
   * Fetches the number of additional listings for each active filter
   *
   * For each active filter, this creates a "what if" scenario: what would the listing
   * count be if we removed just this one filter? The difference between that count and
   * the current count tells users how many more listings they'd see.
   *
   * All fetches happen in parallel for performance.
   */
  const fetchNumListingsExcludedPerFilter = async () => {
    const filterLabelsAndCounts: FilterLabelCounts = {};

    await Promise.all(
      filterLabels.map(async (labelTuple) => {
        const key = labelTuple[0];
        const defaultValue = filterUtils_getDefaultFilterVal(key);
        const filterWithKeyReset = assign({}, desktopFilter, defaultValue);
        setIsLoading(true);
        const numOfListings = await dispatch(
          ListingEngineActions.fetchNumListingsForFilter({ filter: filterWithKeyReset }),
        );

        filterLabelsAndCounts[key] = String(numOfListings);
      }),
    );

    setFilterLabelCounts(filterLabelsAndCounts);
  };

  /**
   * Fetches the total number of listings with no filters applied (default state)
   *
   * This provides the baseline for calculating how restrictive the current filters are.
   * Used to power the "Clear all filters" functionality and show total excluded listings.
   */
  const fetchTotalNumListingsExcluded = async () => {
    try {
      await dispatch(ListingEngineActions.fetchNumDefaultFilterListings());
      setIsLoading(false);
    } catch (err) {
      dispatch(
        ErrorActions.errorHandler({
          error: err,
          errorLocation: 'component.FilterTabs.initializeFilterLabels',
          errorClass: 'listingEngineActions',
        }),
      );
    }
  };

  const initialize = useCallback(() => {
    // Get listings excluded per filter
    fetchNumListingsExcludedPerFilter();

    // Get total listings excluded from all filters
    fetchTotalNumListingsExcluded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearAllFilters = () => yieldCallback(dispatch(DEPRECATED_FilterActions.resetFilterAndNotify()));

  const _yieldFilterItemRemovalDesktop = yieldCallback((key: string) => {
    // @ts-expect-error - DEPRECATED_FilterActions.partialResetFilter types not yet defined
    dispatch(DEPRECATED_FilterActions.partialResetFilter({ key }));
  });

  const handleFilterItemRemoval = (key: string) => {
    _yieldFilterItemRemovalDesktop(key);
  };

  return {
    initialize,
    handleClearAllFilters,
    handleFilterItemRemoval,
    filterLabels,
    filterLabelCounts,
    isLoading,
  };
};
